<template>
    <v-container fluid class="pa-2">
        <v-card v-for="(field, fieldKey) in section.fields" :key="fieldKey" class="mb-3" outlined>
            <v-card-text class="py-2 px-3">
                <v-container fluid class="pa-0">
                    <v-row align="center">

                        <!-- Select Model (always visible) -->
                        <v-col cols="12" md="4" class="pr-md-2" v-if="!field.isCustom && !field.isComposite">
                            <v-autocomplete
                                v-model="field.modelName"
                                :items="sortModels(models)"
                                placeholder="Model"
                                clearable
                                outlined
                                dense
                                hide-details
                                @change="field.fieldName = ''; selectModel(field.modelName)"
                            ></v-autocomplete>
                        </v-col>

                        <!-- Select column (always visible) -->
                        <v-col cols="12" md="4" class="pr-md-2" v-if="!field.isCustom && (!field.isComposite || field.isAssociation)">
                            <v-autocomplete
                                v-model="field.fieldName"
                                :items="field.isAssociation ? [...(sortColumns(field.modelName) || []), 'all'] : sortColumns(field.modelName)"
                                placeholder="Column"
                                clearable
                                outlined
                                dense
                                hide-details
                                @change="field.displayType = 'default'"
                            >
                                <template v-if="field.isAssociation" v-slot:item="{ item }">
                                    <span v-if="item === 'all'">Whole model (works only with custom component)</span>
                                    <span v-else>{{ item }}</span>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Action buttons (right-aligned) -->
                        <v-col class="d-flex justify-end align-center">
                            <v-btn icon small title="Move up" @click.native.stop="moveField('up', fieldKey)">
                                <v-icon>arrow_upward</v-icon>
                            </v-btn>
                            <v-btn icon small title="Move down" @click.native.stop="moveField('down', fieldKey)">
                                <v-icon>arrow_downward</v-icon>
                            </v-btn>
                            <v-btn icon small title="Delete" class="ies-coral--text" @click.native.stop="deleteField(fieldKey)">
                                <v-icon>delete</v-icon>
                            </v-btn>
                            <v-btn small text @click.native.stop="toggleFieldExpanded(fieldKey)">
                                <v-icon small>{{ expandedFields[fieldKey] ? 'expand_less' : 'expand_more' }}</v-icon>
                                {{ expandedFields[fieldKey] ? 'Collapse' : 'Expand' }}
                            </v-btn>
                        </v-col>

                    </v-row>
                </v-container>
            </v-card-text>

            <!-- Expandable options section -->
            <v-expand-transition>
                <div v-show="expandedFields[fieldKey]">
                    <v-divider></v-divider>
                    <v-card-text>
                        <v-container fluid>
                            <v-row>

                                <!-- Composite field -->
                                <v-col cols="12" md="5" class="mb-3">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Display Name</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.displayName"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Used to provide a field name for the system to use when referencing the column, regardless of its display name for human peoples -->
                                <v-col cols="12" md="5" class="mb-3">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Alias Field Name</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.alias"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- select display type -->
                                <!--its better here in v-if to refactor and say when we DO NOT want to display it, but at that point im not sure when it could be-->
                                <v-col cols="12" md="5" class="mb-3" v-if="(field.fieldName && field.fieldName != '' && !field.isCustom) || (field.models && field.models.length > 0) || (field.isAssociation || field.isComposite)">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Display Type</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-autocomplete
                                                v-model="field.displayType"
                                                :items="allDisplayTypes(field)"
                                                item-text="name"
                                                item-value="value"
                                                outlined
                                                dense
                                            ></v-autocomplete>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Select Width -->
                                <v-col cols="12" md="5" class="mb-3" v-if="design.page_mode == '2'">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Field Width</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details type="number" min="1" max="12" placeholder="12" v-model="field.fieldWidth"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Required switch only visible where required statement was implemented -->
                                <v-col cols="12" md="3" class="mb-3 designer-switch"
                                        v-if="['custom', 'text', 'password', 'integer', 'email', 'tinymce', 'htmlinput'].includes(field.displayType)">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Required?</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-switch v-model="field.required" light></v-switch>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Disabled switch only visible where required statement was implemented -->
                                <v-col cols="12" md="3" class="mb-3 designer-switch"
                                        v-if="['text', 'password', 'integer', 'email', 'textarea', 'htmlinput'].includes(field.displayType)">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Disabled?</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-switch color="red" v-model="field.disabled" light></v-switch>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col cols="12" class="mb-3" v-if="design.page_mode == '2'">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Pre-Notice</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.introduction"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col cols="12" class="mb-3" v-if="design.page_mode == 2">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Information</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.information"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col cols="12" class="mb-3" v-if="design.page_mode == 2">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Warning</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.warning"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col cols="12" class="mb-3" v-if="design.page_mode == 2">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Post-Notice</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.outroduction"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Composite field -->
                                <v-col cols="12" class="mb-3">
                                    <v-row>
                                        <v-col cols="12" md="2">
                                            <label>Validation</label>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-row v-for="(validation, ruleIndex) in field.validation" :key="ruleIndex" class="mb-1">
                                                <v-col cols="12" sm="3">
                                                    <v-select
                                                        v-model="validation.ruleType"
                                                        :items="[
                                                            { text: 'Please Select Type', value: '' },
                                                            { text: 'Minimum Length', value: 'minLength' },
                                                            { text: 'Maximum Length', value: 'maxLength' },
                                                            { text: 'Minimum Value', value: 'minValue' },
                                                            { text: 'Maximum Value', value: 'maxValue' },
                                                            { text: 'Not Empty', value: 'notEmpty' },
                                                            { text: 'Regular Expression', value: 'regEx' },
                                                            { text: 'Newsletter Subscriptions', value: 'newsletterSubscriptions' },
                                                            { text: 'Date', value: 'date' },
                                                        ]"
                                                        label="Rule Type"
                                                        outlined
                                                        dense
                                                    ></v-select>
                                                </v-col>
                                                <v-col cols="12" sm="3">
                                                    <v-text-field outlined dense hide-details label="Value (where applicable)" v-model="validation.value"></v-text-field>
                                                </v-col>
                                                <v-col cols="12" sm="1">
                                                    <v-btn v-on:click="removeValidation(fieldKey, ruleIndex)">- Remove</v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-btn v-on:click="addValidation(fieldKey)">+ Add Rule</v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col cols="12" class="mb-3" v-if="(field.fieldName && field.fieldName != '' && !field.isCustom) || (field.models && field.models.length > 0) || (field.isAssociation || field.isComposite)">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Aggregation</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-select
                                                v-model="field.aggregation"
                                                :items="[
                                                    { text: 'None - use Raw Value', value: '' },
                                                    { text: 'Average', value: 'AVG' },
                                                    { text: 'Count', value: 'COUNT' },
                                                    { text: 'Max', value: 'MAX' },
                                                    { text: 'Min', value: 'MIN' },
                                                    { text: 'Sum', value: 'SUM' },
                                                    { text: 'Year', value: 'YEAR' },
                                                ]"
                                                outlined
                                                dense
                                            ></v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Text box custom component -->
                                <v-col cols="12" class="mb-3" v-if="field.displayType && field.displayType == 'custom'">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Custom Component</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-text-field outlined dense hide-details v-model="field.customComponent"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Composite field -->
                                <v-col cols="12" class="mb-3" v-if="field.isComposite">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <label>Model A</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-autocomplete
                                                v-model="field.modelNameA"
                                                :items="sortModels(models)"
                                                placeholder="Please Select Model A"
                                                clearable
                                                outlined
                                                dense
                                                hide-details
                                                @change="field.fieldNameA = ''; selectModel(field.modelNameA)"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <label>Column A</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-autocomplete
                                                v-model="field.fieldNameA"
                                                :items="sortColumns(field.modelNameA)"
                                                placeholder="Please Select Column A"
                                                clearable
                                                outlined
                                                dense
                                                @change="field.displayType = 'default'"
                                            ></v-autocomplete>
                                        </v-col>
                                    </v-row>
                                    <v-row class="mt-2">
                                        <v-col cols="12" sm="4">
                                            <label>Model B</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-autocomplete
                                                v-model="field.modelNameB"
                                                :items="sortModels(models)"
                                                placeholder="Please Select Model B"
                                                clearable
                                                outlined
                                                dense
                                                hide-details
                                                @change="field.fieldNameB = ''; selectModel(field.modelNameB)"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <label>Column B</label>
                                        </v-col>
                                        <v-col cols="12" sm="8">
                                            <v-autocomplete
                                                v-model="field.fieldNameB"
                                                :items="sortColumns(field.modelNameB)"
                                                placeholder="Please Select Column B"
                                                clearable
                                                outlined
                                                dense
                                                @change="field.displayType = 'default'"
                                            ></v-autocomplete>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <!-- Additional relationships -->
                                <v-col cols="12" class="mb-3" v-if="showAdditionalRelationships(field)">
                                    <v-row>
                                        <v-col cols="12">
                                            <strong>Enter relationships between primary model and the model that you want to display in this field.</strong>
                                        </v-col>
                                    </v-row>

                                    <v-row v-for="(relation, index) in field.fieldRelationships" :key="index" class="mb-1">
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete
                                                :items="modelFieldNames"
                                                v-model="relation.modelFieldA"
                                                label="Model-field A"
                                                item-value="value"
                                                outlined
                                                dense
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <v-select
                                                :items="joinTypes"
                                                v-model="relation.joinType"
                                                label="Join Type"
                                                item-value="value"
                                                outlined
                                                dense
                                            ></v-select>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete
                                                :items="modelFieldNames"
                                                v-model="relation.modelFieldB"
                                                label="Model-field B"
                                                item-value="value"
                                                outlined
                                                dense
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="1" class="mt-2">
                                            <v-btn v-on:click="removeRelationship(fieldKey, index)"><v-icon>delete</v-icon> Remove</v-btn>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col cols="12">
                                            <v-btn v-on:click="addRelationship(fieldKey)"><v-icon>add</v-icon> Add Relationship</v-btn>
                                        </v-col>
                                    </v-row>
                                    <v-card-actions>
                                        <v-btn v-on:click="removeAllRelationships(fieldKey)" :disabled="getFieldRelationshipsLength(fieldKey) == 0"><v-icon>cancel</v-icon> Remove Relationships</v-btn>
                                    </v-card-actions>
                                </v-col>

                            </v-row>
                        </v-container>
                    </v-card-text>
                </div>
            </v-expand-transition>
        </v-card>
    </v-container>
</template>

<script>
    export default {
        name: 'PortalDesignerFields',
        props: ['section', 'sectionkey', 'used_models', 'models', 'field_display_types', 'modelFieldNames', 'joinTypes', 'primaryModel', 'design'],
        data () {
            return {
                'currentSection' : this.section,
                'sectionKey' : this.sectionkey,
                'fieldDisplayTypes' : this.field_display_types,
                'fieldCount': this.section.fields.length,
                'runCount': 0,
                'expandedFields': {}
            }
        },
        mounted: function(){
            console.log("page design:", this.design);
        },
        methods: {
            updateObject: function(){
                this.section = this.currentSection;
            },
            toggleFieldExpanded: function(fieldKey) {
                this.$set(this.expandedFields, fieldKey, !this.expandedFields[fieldKey]);
            },
            selectModel: function(modelName){
                if(this.runCount < this.fieldCount){
                    this.runCount +=1;
                    return;
                }
                if(modelName != ''){
                    this.used_models.push(modelName);
                }
            },

            /**
             * Returns an alphabetised array of names of provided models
             * @param models - an object containing models
             */
            sortModels: function(models){
                let keys = Object.keys(models).sort();
                return keys;
            },

            /**
             * Returns an alphabetised array of fields on a given model
             * @param string modelName - the name of a model
             */
            sortColumns: function(modelName){
                var fields = this.models[modelName];
                if (fields) {
                    let keys = Object.keys(fields).sort();
                    fields = keys;
                }
                return  fields;
            },
            allDisplayTypes: function(field){
                const base = [
                    { name: 'Default', value: 'default' },
                    { name: 'Custom', value: 'custom' },
                ];
                return base.concat(this.selectDisplayTypes(field));
            },
            selectDisplayTypes: function(field){

                if (field.isComposite) {
                   return this.field_display_types['composite'];
               }

               if (field.isAssociation) {
                   return this.field_display_types['association'];
               }

               var fields = this.models[field.modelName];

               if(fields){
                   var dataType = fields[field.fieldName]?.dataType;
                   if(dataType){
                       if(this.field_display_types[dataType]){
                           return this.field_display_types[dataType];
                       }
                   }
               }
            },
            deleteField: function(field){
               delete this.section['fields'][field];
               this.section['fields'] =  this.section['fields'].filter(function(n){ return n != undefined });
               this.currentSection = this.section;
            },
            moveField: function(direction, fieldKey) {
               //determine desintation

               if(direction == 'up'){
                   var destinationKey = fieldKey-1
               }
               else{
                   var destinationKey = fieldKey+1
               }

               if( this.section['fields'][destinationKey]){
                   // get fields
                   var fieldToMove = this.section['fields'][fieldKey];
                   var fieldToReplace = this.section['fields'][destinationKey];

                   // swap fields
                   this.section['fields'][fieldKey] = fieldToReplace;
                   this.section['fields'][destinationKey] = fieldToMove

                   // update to refresh render
                   this.currentSection = this.section;
                   this.section['fields'] =  this.section['fields'].filter(function(n){ return n != undefined });
               }

            },

            addValidation: function(fieldKey){
                console.log("adding validation");
                var field = this.section.fields[fieldKey];
                if (!field.validation){
                    field.validation = [];
                }
                field.validation.push({"ruleType": "", "value": ""});
                console.log(field);
            },

            removeValidation: function(fieldKey, index){
                var field = this.section.fields[fieldKey];
                console.log(field);
                field.validation.splice(index, 1);
            },

            /**
             * This method checks current field and decides if it should display section when we can add additional relationships
             * between primary model and this field.
             *
             * Show this field if:
             *  1) it's association field
             *  2) it's composite field and it's using foreign model
             *
             *  @returns boolean
             */
            showAdditionalRelationships(field) {
                if (field.hasOwnProperty('isAssociation')) {
                    if (field.isAssociation) {
                        return true;
                    }
                }

                if (field.hasOwnProperty('isComposite')) {
                    let primary = this.primaryModel;
                    if (field.isComposite && (primary !== field.modelNameA || primary !== field.modelNameB) &&
                        (field.modelNameA !== '' && field.modelNameB !== '')) {
                        return true;
                    }
                }
            },
            /**
             * Add blank relationship to this field
             * @param fieldKey
             */
            addRelationship(fieldKey) {
                // get currect field
                var field = this.section.fields[fieldKey];

                // fallback if this field doesn't have fieldRelationship because it's old field, add it
                if (!field.hasOwnProperty('fieldRelationships')) {
                    field['fieldRelationships'] = [];
                }

                // add new relationship to this field
                field.fieldRelationships.push({ 'modelFieldA': '', 'joinType': '', 'modelFieldB': '' });
            },

            /**
             * Remove clicked relationship from this field
             * @param fieldKey
             * @param index
             */
            removeRelationship(fieldKey, index) {
                // get currect field
                var field = this.section.fields[fieldKey];

                field.fieldRelationships.splice(index, 1);
            },
            /**
             * Get number of relationship that this field has
             * @param fieldKey
             * @returns {number}
             */
            getFieldRelationshipsLength(fieldKey) {
                var field = this.section.fields[fieldKey];

                // fallback if this field doesn't have fieldRelationship because it's old field, add it
                if (!field.hasOwnProperty('fieldRelationships')) {
                    field['fieldRelationships'] = [];
                }

                return field.fieldRelationships.length;
            },
            /**
             * Remove all relationships from current field
             * @param fieldKey
             */
            removeAllRelationships(fieldKey) {
                // get currect field
                var field = this.section.fields[fieldKey];

                field.fieldRelationships = [];
            },
        },


}
</script>

<style>
    .designer-switch {
        margin: 0;
        max-height: 40px;
    }

</style>
